import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Label, Switch, toast } from '@utima/ui';
import { HelpCircle } from 'lucide-react';
import { memo, useEffect, useState, type ReactNode } from 'react';

import { Card } from '@/components/card/Card';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { trpc } from '@/services/trpc';

export type PartnerManagementCardProps = {
  clientOrganizationId: string;
};

function SectionTitle({ label, hint }: { label: ReactNode; hint: ReactNode }) {
  return (
    <div className='flex items-center gap-1.5'>
      <h3 className='text-sm font-semibold'>{label}</h3>
      <SimpleTooltip title={hint} delayDuration={200}>
        <button
          type='button'
          className='inline-flex text-muted-foreground hover:text-foreground'
          aria-label='More information'
        >
          <HelpCircle className='size-3.5' />
        </button>
      </SimpleTooltip>
    </div>
  );
}

/**
 * SuperAdmin partner controls for an organization, consolidated in one place:
 *  - "Partner hub": mark this org itself as a reseller hub (`isPartnerOrganization`).
 *  - "Managed by partner": link this org as a client of some hub (`PartnerClientLink`).
 * These are mutually exclusive — hubs cannot nest under other hubs.
 */
export const PartnerManagementCard = memo(function PartnerManagementCard({
  clientOrganizationId,
}: PartnerManagementCardProps) {
  const { t } = useLingui();
  const utils = trpc.useUtils();

  const { data: organization } = trpc.organizations.findOne.useQuery({
    id: clientOrganizationId,
  });
  const { data: link } = trpc.partnerLinks.getForClient.useQuery({
    clientOrganizationId,
  });
  const { data: organizations } = trpc.organizations.findAll.useQuery({
    pagination: { pageSize: 1000 },
  });

  const [selectedHubId, setSelectedHubId] = useState<string>('');
  const [selectedRepId, setSelectedRepId] = useState<string>('');

  useEffect(() => {
    setSelectedHubId(link?.partnerOrganizationId ?? '');
    setSelectedRepId(link?.createdByUserId ?? '');
  }, [link?.partnerOrganizationId, link?.createdByUserId]);

  const { data: hubAdmins } = trpc.partnerLinks.listHubAdmins.useQuery(
    { partnerOrganizationId: selectedHubId },
    { enabled: !!selectedHubId },
  );

  const updateOrganization = trpc.organizations.update.useMutation({
    onSuccess: async () => {
      toast.success(t`Partner hub setting updated.`);
      await utils.organizations.findOne.invalidate({
        id: clientOrganizationId,
      });
      await utils.organizations.findAll.invalidate();
    },
    onError: (error: { message: string }) => {
      toast.error(t`Failed to update partner hub setting: ${error.message}`);
    },
  });

  const setClientPartner = trpc.partnerLinks.setClientPartner.useMutation({
    onSuccess: async () => {
      toast.success(t`Partner assignment updated.`);
      await utils.partnerLinks.getForClient.invalidate({
        clientOrganizationId,
      });
    },
    onError: (error: { message: string }) => {
      toast.error(t`Failed to update partner assignment: ${error.message}`);
    },
  });

  const isPartnerHub = organization?.isPartnerOrganization ?? false;
  const isManagedByPartner = !!link?.partnerOrganizationId;
  // Hubs and client links are mutually exclusive. If both somehow exist, show
  // both sections so a SuperAdmin can untangle the state.
  const showPartnerHubSection = !isManagedByPartner || isPartnerHub;
  const showManagedBySection = !isPartnerHub || isManagedByPartner;

  const partnerHubs = (organizations?.items ?? []).filter(
    org => org.isPartnerOrganization && org.id !== clientOrganizationId,
  );

  const handleHubToggle = (checked: boolean) => {
    if (checked && isManagedByPartner) {
      return;
    }

    updateOrganization.mutate({
      id: clientOrganizationId,
      isPartnerOrganization: checked,
    });
  };

  const handleSave = () => {
    if (isPartnerHub && selectedHubId) {
      toast.error(
        t`A partner hub cannot be managed by another partner. Disable Partner hub first.`,
      );

      return;
    }

    setClientPartner.mutate({
      clientOrganizationId,
      partnerOrganizationId: selectedHubId || null,
      createdByUserId: selectedRepId || null,
    });
  };

  const formatAdmin = (admin: {
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  }) => {
    const name = [admin.firstName, admin.lastName].filter(Boolean).join(' ');

    if (name && admin.email) {
      return `${name} (${admin.email})`;
    }

    return name || admin.email || t`Unnamed user`;
  };

  const handleClear = () => {
    setClientPartner.mutate({
      clientOrganizationId,
      partnerOrganizationId: null,
    });
  };

  const isDirty =
    (link?.partnerOrganizationId ?? '') !== selectedHubId ||
    (link?.createdByUserId ?? '') !== selectedRepId;

  return (
    <Card title={<Trans>Partner</Trans>}>
      <div className='flex flex-col gap-6'>
        {showPartnerHubSection && (
          <section className='flex flex-col gap-3'>
            <SectionTitle
              label={<Trans>Partner hub</Trans>}
              hint={t`Marks this organization as a reseller hub. Partner hubs cannot be nested under another partner, so Managed by partner is unavailable while this is on.`}
            />
            <div className='flex items-start gap-3'>
              <Switch
                id='partner-hub-switch'
                checked={isPartnerHub}
                onCheckedChange={handleHubToggle}
                disabled={updateOrganization.isPending || isManagedByPartner}
              />
              <Label
                htmlFor='partner-hub-switch'
                className='text-sm text-muted-fg'
              >
                <Trans>
                  Mark this organization as a partner hub (reseller). All of its
                  admins can provision and operate client organizations linked
                  to this hub.
                </Trans>
              </Label>
            </div>
          </section>
        )}

        {showManagedBySection && (
          <section
            className={`flex flex-col gap-3 ${showPartnerHubSection ? 'border-t pt-6' : ''}`}
          >
            <SectionTitle
              label={<Trans>Managed by partner</Trans>}
              hint={t`Assigns this organization as a client of a partner hub. Client orgs cannot themselves be partner hubs, so Partner hub is unavailable while a partner link exists.`}
            />
            <p className='text-sm text-muted-fg'>
              <Trans>
                Assign this client organization to a partner hub. All admins of
                that hub will be able to see, switch to, and manage this
                organization.
              </Trans>
            </p>
            <div className='flex flex-col gap-1'>
              <Label className='text-sm font-medium'>
                <Trans>Partner hub</Trans>
              </Label>
              <select
                data-cta='admin-organization-managed-by-partner-select'
                className='h-9 max-w-xs rounded-radius border border-border bg-background px-3 text-sm'
                value={selectedHubId}
                onChange={e => {
                  setSelectedHubId(e.target.value);
                  setSelectedRepId('');
                }}
                disabled={setClientPartner.isPending}
              >
                <option value=''>{t`No partner`}</option>
                {partnerHubs.map(hub => (
                  <option key={hub.id} value={hub.id}>
                    {hub.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedHubId && (
              <div className='flex flex-col gap-1'>
                <Label className='text-sm font-medium'>
                  <Trans>Attributed rep (optional)</Trans>
                </Label>
                <select
                  data-cta='admin-organization-attributed-rep-select'
                  className='h-9 max-w-xs rounded-radius border border-border bg-background px-3 text-sm'
                  value={selectedRepId}
                  onChange={e => setSelectedRepId(e.target.value)}
                  disabled={setClientPartner.isPending}
                >
                  <option value=''>{t`No attributed rep`}</option>
                  {(hubAdmins ?? []).map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {formatAdmin(admin)}
                    </option>
                  ))}
                </select>
                <p className='text-xs text-muted-fg'>
                  <Trans>
                    The hub member credited for this client (commission
                    reporting and the “Mine only” filter). This is attribution
                    only — it does not restrict access; all hub admins can
                    manage this org.
                  </Trans>
                </p>
              </div>
            )}

            <div className='flex flex-wrap items-center gap-2'>
              <Button
                variant='primary'
                size='sm'
                onClick={handleSave}
                disabled={!isDirty || setClientPartner.isPending}
              >
                <Trans>Save</Trans>
              </Button>
              {link && (
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={handleClear}
                  disabled={setClientPartner.isPending}
                >
                  <Trans>Remove link</Trans>
                </Button>
              )}
            </div>
          </section>
        )}
      </div>
    </Card>
  );
});
